class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

/* Desktop pointers open the header menus on hover; touch and small screens keep tapping. */
const HEADER_MENU_HOVER_MEDIA = '(min-width: 990px) and (hover: hover)';
/* Grace period so the pointer can cross the gap between the link and the panel. */
const HEADER_MENU_HOVER_CLOSE_DELAY = 200;
/* Scrolling means the user is done with the menu. Tolerance keeps momentum and
   rubber-band scroll from slamming a just-opened panel shut. */
const HEADER_MENU_SCROLL_CLOSE_THRESHOLD = 40;

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.hoverMediaQuery = window.matchMedia(HEADER_MENU_HOVER_MEDIA);
    this.closeTimeout = null;

    this.onScrollHandler = this.onScroll.bind(this);
    this.scrollAnchor = 0;

    this.addEventListener('mouseenter', this.onHoverEnter.bind(this));
    this.addEventListener('mouseleave', this.onHoverLeave.bind(this));
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
  }

  get hoverEnabled() {
    return this.hoverMediaQuery.matches;
  }

  onHoverEnter() {
    if (!this.hoverEnabled) return;
    this.cancelPendingClose();
    this.open();
  }

  onHoverLeave() {
    if (!this.hoverEnabled) return;
    this.cancelPendingClose();
    this.closeTimeout = setTimeout(() => this.close(), HEADER_MENU_HOVER_CLOSE_DELAY);
  }

  onSummaryClick(event) {
    /* Hover already opened it — a mouse click would only toggle it shut.
       Keyboard activation (Enter/Space reports detail 0) still toggles. */
    if (!this.hoverEnabled || event.detail === 0) return;
    event.preventDefault();
  }

  cancelPendingClose() {
    if (!this.closeTimeout) return;
    clearTimeout(this.closeTimeout);
    this.closeTimeout = null;
  }

  /* An open panel sets header.preventHide, which stops StickyHeader from ever
     reaching hide() — the one place that would have closed this menu. Without
     this listener the panel stays pinned over the page for the whole scroll. */
  onScroll() {
    if (Math.abs(window.scrollY - this.scrollAnchor) < HEADER_MENU_SCROLL_CLOSE_THRESHOLD) return;
    this.close();
  }

  open() {
    if (this.mainDetailsToggle.hasAttribute('open')) return;
    /* One panel at a time. They overlap, so a sibling still inside its close
       delay would stack two menus' content on top of each other. */
    document.querySelectorAll('header-menu').forEach((menu) => {
      if (menu !== this) menu.close();
    });
    this.mainDetailsToggle.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', true);
    this.scrollAnchor = window.scrollY;
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
  }

  close() {
    window.removeEventListener('scroll', this.onScrollHandler);
    this.cancelPendingClose();
    super.close();
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
